import React, { Suspense, lazy, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import NavMenu from "../../shared/NavBar/NavBar.jsx";
import Footer from "@shared/Footer/Footer";
import HeroBadge from "@shared/HeroBadge/HeroBadge";
import ContextHeroSection from "../Home/Sections/ContextHeroSection/ContextHeroSection";
import dashboardHero from "@assets/images/iltacon/dashboard-hero.png";
import "./ILTACON.css";

// Same before/after stack chart the landing page uses, under ILTACON framing.
// Lazy so its ~28 tool logos stay out of the hero's critical path.
const CompetitiveSection = lazy(() => import("../Home/CompetitiveSection/CompetitiveSection.jsx"));

// Same typing treatment as the landing hero (HeroSection): one character every
// 40ms, with the emphasised word rendered in italics as it is typed out.
const HERO_TITLE = "Stop wasting time & money micromanaging legal software";
const HERO_ITALIC_WORD = "time & money";
const HERO_ITALIC_START = HERO_TITLE.indexOf(HERO_ITALIC_WORD);
const HERO_ITALIC_END = HERO_ITALIC_START + HERO_ITALIC_WORD.length;

const ARROW = "↗︎"; // U+FE0E keeps the arrow as text, not an emoji tile

const howItWorksChecks = [
  "Case context that carries across email, files, tasks, dockets, and deadlines",
  "Docket and deadline monitoring that reaches you as an email, not another dashboard",
  "Citation and quote checks before anything moves forward",
  "Nothing is filed, sent, or finalized without a person approving it",
];

const howItWorksSteps = [
  ["01", "Connect your account", "Take 5 seconds to connect your Outlook or Gmail."],
  ["02", "Vienna does the legwork", "Pulls the case context together, drafts, checks citations, tracks what’s due."],
  ["03", "You review and approve", "Agent sends drafts. Your team keeps the judgment call."],
];

const audienceCards = [
  ["Mid- & Big-Law Litigation partners", "Case status without chasing anyone, deadlines you don’t have to hold in your head, drafts that start at 80%."],
  ["GCs managing outside counsel", "Follow ongoing litigation handled by external teams. Updates, docket monitoring, and case management automated."],
  ["Paralegals & Associates", "Support litigators by building factual chronologies, research, first drafts, citation checks, & document organization."],
  ["Practice Management & Firm Operators", "Intake, matter setup, timekeeping, and team assignment in the same place as the case."],
];

function ILTACON() {
  const canonicalUrl = "https://nougat.law/iltacon-2026";
  const [typedIndex, setTypedIndex] = useState(0);

  useEffect(() => {
    if (typedIndex >= HERO_TITLE.length) return undefined;
    const timeout = setTimeout(() => setTypedIndex((prev) => prev + 1), 40);
    return () => clearTimeout(timeout);
  }, [typedIndex]);

  const renderHeroTitle = () => {
    const typed = HERO_TITLE.slice(0, typedIndex);
    return (
      <>
        {typed.slice(0, HERO_ITALIC_START)}
        <em>{typed.slice(HERO_ITALIC_START, HERO_ITALIC_END)}</em>
        {typed.slice(HERO_ITALIC_END)}
      </>
    );
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "ILTACON 2026 | Vienna Case & Litigation Management",
    description:
      "Meet Vienna at ILTACON 2026. One system for case and litigation management that runs from your inbox, with citation and quote checks and human review before anything moves.",
    url: canonicalUrl,
    publisher: {
      "@type": "Organization",
      name: "Nougat",
      url: "https://nougat.law",
    },
    about: {
      "@type": "Service",
      name: "Vienna case and litigation management",
      serviceType: "Legal workflow automation",
      areaServed: "Worldwide",
    },
  };

  return (
    <div className="iltacon-page">
      <Helmet>
        <title>ILTACON 2026 | Case &amp; Litigation Management That Runs From Your Inbox | Nougat</title>
        <meta
          name="description"
          content="Meet Vienna at ILTACON 2026. Replace a dozen passive litigation and practice management tools with one that runs from your inbox—no new system to learn."
        />
        <meta
          name="keywords"
          content="ILTACON 2026, litigation management, case management, legal technology, docket monitoring, legal workflow automation, email-first AI, Vienna"
        />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Nougat" />
        <meta property="og:title" content="ILTACON 2026 | Case & Litigation Management That Runs From Your Inbox" />
        <meta
          property="og:description"
          content="One system for case and litigation management, run from the inbox your team already uses. Meet Vienna at ILTACON 2026."
        />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content="https://nougat.law/social-preview.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="ILTACON 2026 | Case & Litigation Management That Runs From Your Inbox" />
        <meta
          name="twitter:description"
          content="One system for case and litigation management, run from the inbox your team already uses. Meet Vienna at ILTACON 2026."
        />
        <meta name="twitter:image" content="https://nougat.law/social-preview.png" />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>
      <NavMenu />
      <main id="main-content">

        <div className="iltacon-content">
          <section className="iltacon-hero">
            {/* Same pulsating grid vectors the landing hero uses. */}
            <div className="iltacon-vector iltacon-vector--top-left" />
            <div className="iltacon-vector iltacon-vector--bottom-right" />

            <div className="iltacon-hero-content">
              <div className="iltacon-hero-copy">
                <HeroBadge text="Hire a Legal Operations Agent" />

                <h1 className="iltacon-hero-title">
                  {renderHeroTitle()}
                  <span className="iltacon-cursor" aria-hidden="true">|</span>
                </h1>

                <p className="iltacon-hero-lede">
                  Recover 15% of billable time by managing all your tools over email, Slack, Teams,
                  wherever you already work. All your firm&apos;s tools connect
                  in one confidential place. Vienna gives your team an AI software manager for ROI
                  and adoption of procured tools: with no behavior change required and one-click setup.
                </p>

                <div className="iltacon-hero-actions">
                  <a className="iltacon-button iltacon-button-light" href="https://calendly.com/ali-juristai/demo" target="_blank" rel="noreferrer">Book an ILTACON conversation <span>{ARROW}</span></a>
                  <a className="iltacon-text-link iltacon-text-link-light" href="#how-it-works">See how it works <span>↓</span></a>
                </div>

                <p className="iltacon-hero-note">A focused 20-minute conversation · Leave with one workflow scoped and a pilot plan</p>
              </div>

              <div className="iltacon-hero-visual">
                <img
                  src={dashboardHero}
                  alt="Vienna dashboard showing a Civil Law overview with upcoming deadlines, legal team, and open and closed cases"
                  width="1683"
                  height="1365"
                  loading="eager"
                  decoding="async"
                />
              </div>
            </div>
          </section>
        </div>

        <section className="iltacon-section iltacon-panel-section">
          <div className="iltacon-shell">
            <div className="iltacon-section-heading">
              <div>
                <HeroBadge text="The adoption bottleneck" className="primary iltacon-pill" />
                <h2>Legal tech fails when it creates more work.</h2>
              </div>
              <p className="iltacon-side-copy">If your team is still copy-pasting across billing, case management, legal AI, and emails, your tech stack is slowing you down. Vienna consolidates all of those tools' context to keep your legal team moving, without asking your lawyers to change how they work.</p>
            </div>
            {/* Landing-page context graphic, reused verbatim — it already carries the
                Context Switching / Missing / Stitching stats. Its own header is
                hidden because this section supplies the heading above. */}
            <div className="iltacon-context-embed">
              <ContextHeroSection />
            </div>
          </div>
        </section>

        <section className="iltacon-section iltacon-solution" id="how-it-works">
          <div className="iltacon-shell iltacon-solution-grid">
            <div>
              <HeroBadge text="How it works" className="primary iltacon-pill" />
              <h2>Our agent operates in your inbox.</h2>
              <p className="iltacon-body-copy">Vienna’s email agent proactively works on your cases and emails you draft work product from its email address. Forward an email, upload a draft, or start with a docket number. Vienna builds a durable record of the case. The agent then monitors, summarizes, researches, drafts, routes, and reports.</p>
              <ul className="iltacon-check-list">
                {howItWorksChecks.map((check) => <li key={check}>{check}</li>)}
              </ul>
            </div>
            <div className="iltacon-steps-card">
              {howItWorksSteps.map(([number, title, body]) => (
                <div className="iltacon-step" key={number}>
                  <span>{number}</span>
                  <div><strong>{title}</strong><p>{body}</p></div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="iltacon-promotion">
          <div className="iltacon-shell iltacon-promotion-inner">
            <HeroBadge text="ILTA promotion" className="primary iltacon-pill" />
            <h2><span className="iltacon-promotion-highlight">Implementation fee — waived.</span></h2>
            <p className="iltacon-promotion-copy">Talk with us at ILTACON. If the fit is right, we’ll waive the standard implementation fee for teams that scope a pilot by September 27th. Subscription rates still apply.</p>
          </div>
        </section>

        <section className="iltacon-section iltacon-audience">
          <div className="iltacon-shell">
            <div className="iltacon-section-heading">
              <div>
                <HeroBadge text="Who it’s for" className="primary iltacon-pill" />
                <h2>Same platform. Different jobs.</h2>
              </div>
              <p className="iltacon-side-copy">Start with the part your team feels most. Add the rest when it’s earning its keep.</p>
            </div>
            <div className="iltacon-card-grid iltacon-four-up">
              {audienceCards.map(([title, body]) => (
                <article className="iltacon-audience-card" key={title}><h3>{title}</h3><p>{body}</p><span>→</span></article>
              ))}
            </div>
          </div>
        </section>

        <Suspense fallback={null}>
          <CompetitiveSection
            eyebrow="What we collapse"
            title="Here is everything we collapse into your inbox"
            subhead="Thirteen tools’ worth of litigation and firm management context, in one matter-native system your team reaches from the email they already use."
          />
        </Suspense>

        <section className="iltacon-cta">
          <div className="iltacon-shell iltacon-cta-inner">
            <div>
              <HeroBadge text="Your ILTACON starting point" className="iltacon-pill" />
              <h2>What's one non-billable manual thing your team does every week? Let's automate that for you.</h2>
            </div>
            <div>
              <p>Map how it works today. Find where the case context gets lost. Scope a pilot with clear owners, human review, and a success measure you’d actually accept.</p>
              <a className="iltacon-button iltacon-button-light" href="https://calendly.com/ali-juristai/demo" target="_blank" rel="noreferrer">Book your conversation <span>{ARROW}</span></a>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
}

export default ILTACON;
